import { useQuery } from "@tanstack/react-query";
import { Stethoscope } from "lucide-react";

import type { MedicoPipelineItem } from "@/api/db/medico";
import { orpc } from "@/client";
import { EmptyState } from "@/components/ui/empty-state";
import { SkeletonRows } from "@/components/ui/skeleton";
import { usePageVisible } from "@/hooks/use-page-visible";
import { djs } from "@/lib/djs";
import { format } from "@/lib/format";
import { MedicoLabels } from "@/lib/labels/medico";
import type { ChipTone } from "@/lib/labels/shared";
import { PollCadence } from "@/lib/poll-cadence";
import { cn } from "@/lib/utils";

import { PipelineActions } from "./pipeline-actions";
import { PipelineParts } from "./pipeline-parts";
import { MetaLine, QueueRow, QueueSection } from "./queue-section";

const toneDotClass: Record<ChipTone, string> = {
	amber: "bg-warning",
	petrol: "bg-primary",
	teal: "bg-info",
	coral: "bg-destructive",
};

const toneTextClass: Record<ChipTone, string> = {
	amber: "text-warning",
	petrol: "text-primary",
	teal: "text-info",
	coral: "text-destructive",
};

function StatusDot({
	tone,
	children,
	...rest
}: { tone: ChipTone; children: React.ReactNode } & React.ComponentProps<"span">) {
	return (
		<span
			{...rest}
			className={cn(
				"inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium",
				toneTextClass[tone],
			)}
		>
			<span className={cn("size-1.5 shrink-0 rounded-full", toneDotClass[tone])} />
			{children}
		</span>
	);
}

const timelineDotClass: Record<ChipTone, string> = {
	amber: "bg-warning/50",
	petrol: "bg-primary/50",
	teal: "bg-info/50",
	coral: "bg-destructive/60",
};

const timelineTextClass: Record<ChipTone, string> = {
	amber: "text-foreground/70",
	petrol: "text-foreground/70",
	teal: "text-foreground/70",
	coral: "text-destructive",
};

function PipelineTimeline({ timeline }: { timeline: MedicoPipelineItem["timeline"] }) {
	return (
		<ol data-slot="timeline" className="flex flex-col gap-0.5">
			{timeline.map((entry) => {
				const entryTone = MedicoLabels.pipeline.stageTone[entry.stage];
				const entryLabel = MedicoLabels.pipeline.stageLabel(entry.stage, entry.errorType);

				return (
					<li
						key={`${entry.stage}-${entry.at}`}
						data-stage={entry.stage}
						data-tone={entryTone}
						className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground"
					>
						<span className={cn("size-1 shrink-0 rounded-full", timelineDotClass[entryTone])} />
						<span className={timelineTextClass[entryTone]}>{entryLabel}</span>
						<span>{format.dateTime(entry.at)}</span>
					</li>
				);
			})}
		</ol>
	);
}

function PipelineRow({ item }: { item: MedicoPipelineItem }) {
	const tone = MedicoLabels.pipeline.stageTone[item.stage];
	const stageLabel = MedicoLabels.pipeline.stageLabel(item.stage, item.errorType);
	const anchored = item.patientStatus !== "orphan";
	const openableManualId = anchored ? item.medManualId : null;
	const showManualReady =
		item.manualReady && item.stage !== "ready" && item.stage !== "prontuario_pending" && anchored;
	const elapsedMinutes =
		item.stageSince === null ? null : djs().diff(djs(item.stageSince), "minute");

	return (
		<QueueRow>
			<div
				className="flex flex-col gap-2"
				data-component="pipeline-card"
				data-transcript={item.transcriptId}
			>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1">
					<p className="min-w-0 flex-1 truncate font-display text-sm font-semibold text-foreground">
						{item.name}
					</p>

					{item.patientStatus !== "ok" && (
						<StatusDot
							tone={MedicoLabels.pipeline.patientStatusTone[item.patientStatus]}
							data-slot="patient-status"
						>
							{MedicoLabels.pipeline.patientStatus[item.patientStatus]}
						</StatusDot>
					)}

					<StatusDot tone={tone} data-slot="stage" data-stage={item.stage}>
						{stageLabel}
					</StatusDot>

					{showManualReady && (
						<StatusDot tone="teal" data-slot="manual-ready">
							{MedicoLabels.pipeline.manualReady}
						</StatusDot>
					)}
				</div>

				<MetaLine>
					<span className="font-mono">{format.dateTime(item.recordedAt)}</span>

					{item.transcriptClass !== null && (
						<span>{MedicoLabels.transcriptClass[item.transcriptClass]}</span>
					)}

					{item.partsCount > 1 && <span>{MedicoLabels.pipeline.recordings(item.partsCount)}</span>}

					<span>{MedicoLabels.pipeline.step(item.step, item.total)}</span>

					{item.progressDone !== null && item.progressTotal !== null && (
						<span data-slot="subprogress" className="font-mono">
							{MedicoLabels.pipeline.subprogress(item.progressDone, item.progressTotal)}
						</span>
					)}

					{elapsedMinutes !== null && (
						<span data-slot="elapsed">{MedicoLabels.pipeline.elapsed(elapsedMinutes)}</span>
					)}
				</MetaLine>

				{item.timeline.length > 0 && <PipelineTimeline timeline={item.timeline} />}

				{item.errorReason !== null && (
					<p data-slot="error-reason" className="font-mono text-xs text-destructive">
						{MedicoLabels.jobError(item.errorReason)}
					</p>
				)}

				{item.errorDetail !== null && (
					<pre
						data-slot="error-detail"
						className="max-h-24 overflow-auto rounded-md border border-border bg-background px-2 py-1.5 font-mono text-xs leading-relaxed text-muted-foreground"
					>
						{item.errorDetail}
					</pre>
				)}

				<PipelineActions item={item} openableManualId={openableManualId} />

				{item.partsCount > 1 && <PipelineParts item={item} />}
			</div>
		</QueueRow>
	);
}

export function PipelineQueue() {
	const visible = usePageVisible();

	const { data } = useQuery(
		orpc.medico.pipeline.queryOptions({
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);

	if (data === undefined) {
		return <SkeletonRows rows={4} />;
	}

	return (
		<QueueSection title={MedicoLabels.pipeline.title} subtitle={MedicoLabels.pipeline.subtitle}>
			{data.length === 0 && (
				<EmptyState icon={Stethoscope} title={MedicoLabels.pipeline.emptyTitle} />
			)}

			{data.map((item) => (
				<PipelineRow key={item.groupId} item={item} />
			))}
		</QueueSection>
	);
}
